import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Presentation, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui';
import { Blocks, type Block } from './Blocks';
import { Rich } from './Rich';

export interface DocSection {
  title: string;
  blocks: Block[];
}

interface DocPageProps {
  badgeIcon: LucideIcon;
  badge: string;
  title: string;
  status: string;
  summary: string;
  top?: Block[];
  sections: DocSection[];
  contactEmail: string;
  deckPath?: string;
  footer: string;
  aside?: ReactNode;
}

/** A long-form investor document: the one-pager and the investor overview. */
export function DocPage({
  badgeIcon: BadgeIcon,
  badge,
  title,
  status,
  summary,
  top = [],
  sections,
  contactEmail,
  deckPath,
  footer,
  aside,
}: DocPageProps) {
  return (
    <div>
      <header className="ruled border-b border-line">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 md:py-16">
          <div className="mb-5 inline-flex items-center gap-2 border-b border-accent pb-1">
            <BadgeIcon className="h-4 w-4 text-accent-ink" />
            <span className="label text-accent-ink">{badge}</span>
          </div>
          <h1 className="mb-3 text-section-mobile font-semibold tracking-heading md:text-[2.5rem] md:leading-[1.1]">{title}</h1>
          <p className="label mb-5 normal-case tracking-normal">{status}</p>
          <p className="max-w-3xl text-sm leading-relaxed text-ink-2 sm:text-base">
            <Rich text={summary} />
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 pb-12 pt-8 sm:px-6 md:pt-12">
        {top.length > 0 && (
          <div className="mb-8 space-y-4 md:mb-10">
            <Blocks blocks={top} />
          </div>
        )}

        {sections.map((section, i) => (
          <section key={section.title} className="mb-8 md:mb-10">
            <div className="mb-3 flex items-baseline gap-2.5">
              <span className="font-mono text-[11px] tabular text-accent-ink">{String(i + 1).padStart(2, '0')}</span>
              <h2 className="text-base font-semibold tracking-tight sm:text-lg">{section.title}</h2>
              <span className="ml-1 h-px flex-1 bg-line" />
            </div>
            <div className="space-y-3 sm:space-y-4">
              <Blocks blocks={section.blocks} />
            </div>
          </section>
        ))}

        {aside}

        <div className="flex flex-col gap-3 border-t border-ink pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href={`mailto:${contactEmail}`} className="w-full sm:w-auto">
              <Button variant="primary" className="w-full sm:w-auto" leftIcon={<Mail className="h-4 w-4" />}>
                {contactEmail}
              </Button>
            </a>
            {deckPath && (
              <Link to={deckPath} className="w-full sm:w-auto">
                <Button variant="secondary" className="w-full sm:w-auto" leftIcon={<Presentation className="h-4 w-4" />}>
                  Pitch deck
                </Button>
              </Link>
            )}
          </div>
          <p className="text-xs text-ink-3">{footer}</p>
        </div>
      </div>
    </div>
  );
}
